/**
 * El error de la API, tipado y con `code` estable.
 *
 * La API garantiza que **toda** respuesta no-2xx tiene esta forma
 * (`services/api/errors.py`):
 *
 *     { "error": { "code", "message", "details", "trace_id" } }
 *
 * y que `code` —no el status HTTP— es el contrato. Dos errores distintos
 * comparten status: `invalid_credentials` y `token_expired` son ambos 401 y
 * la UI tiene que hacer cosas opuestas con ellos (mostrar "contraseña
 * incorrecta" vs. refrescar en silencio).
 *
 * Los fallos que no llegan a producir respuesta (red caída, timeout, aborto)
 * también se representan como `ApiError`, con `status: 0` y un `code` propio.
 * Un solo tipo de error significa un solo camino de manejo en cada vista.
 */

/** Códigos que la UI trata de forma especial. Cualquier otro cae en el genérico. */
export const API_ERROR_CODES = [
  // --- del backend
  'invalid_credentials',
  'unauthorized',
  'token_expired',
  'invalid_token',
  'invalid_refresh_token',
  'refresh_token_reused',
  'issuer_not_enabled',
  'forbidden',
  'insufficient_role',
  'not_found',
  'conflict',
  'bad_request',
  'invalid_cursor',
  'validation_error',
  'method_not_allowed',
  'payload_too_large',
  'rate_limited',
  'internal_error',
  'http_error',
  // --- del cliente: no hubo respuesta que parsear
  'network_error',
  'timeout',
  'no_session',
  'malformed_response',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** ¿Este `code` es uno de los que la UI conoce? */
export function isKnownErrorCode(code: string): code is ApiErrorCode {
  return (API_ERROR_CODES as readonly string[]).includes(code);
}

export interface ApiErrorInit {
  status: number;
  code: string;
  message: string;
  details?: Record<string, unknown> | null;
  traceId?: string | null;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  /** Estable. Es lo que se compara en código; el `message` es para humanos. */
  readonly code: string;
  readonly details: Record<string, unknown> | null;
  /** Va en `X-Trace-Id`. Es el número que el usuario pega en el ticket. */
  readonly traceId: string | null;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? null;
    this.traceId = init.traceId ?? null;
  }

  /** 401/403: la sesión o el rol no alcanzan. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** ¿Tiene sentido reintentar solo? Un 4xx no se arregla reintentando. */
  get isRetriable(): boolean {
    if (this.status === 0) return this.code !== 'no_session';
    if (this.status === 429) return true;
    return this.status >= 500;
  }
}

/** Fallos sin respuesta HTTP. */
export function networkError(cause: unknown): ApiError {
  const aborted =
    cause instanceof DOMException && (cause.name === 'AbortError' || cause.name === 'TimeoutError');
  return new ApiError({
    status: 0,
    code: aborted ? 'timeout' : 'network_error',
    message: aborted
      ? 'La petición tardó demasiado y se canceló.'
      : 'No se pudo contactar con la API.',
    cause,
  });
}

interface RawErrorEnvelope {
  error?: {
    code?: unknown;
    message?: unknown;
    details?: unknown;
    trace_id?: unknown;
  };
}

/**
 * Traduce una respuesta no-2xx a `ApiError`.
 *
 * Tolera un cuerpo que no siga el contrato (un 502 del balanceador devuelve
 * HTML): en ese caso se sintetiza un `code` a partir del status. Una vista
 * nunca debería reventar por la forma del error.
 */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  const traceId = response.headers.get('X-Trace-Id');
  let body: RawErrorEnvelope | null = null;
  try {
    body = (await response.json()) as RawErrorEnvelope;
  } catch {
    // Un 502 del balanceador devuelve HTML, no el sobre de error. Se sigue
    // con `body` en null y el `code` se sintetiza a partir del status.
  }

  const raw = body?.error;
  const code = typeof raw?.code === 'string' ? raw.code : statusFallbackCode(response.status);
  const message =
    typeof raw?.message === 'string' && raw.message.length > 0
      ? raw.message
      : `La API respondió ${response.status}.`;
  const details =
    typeof raw?.details === 'object' && raw.details !== null
      ? (raw.details as Record<string, unknown>)
      : null;

  return new ApiError({
    status: response.status,
    code,
    message,
    details,
    traceId: typeof raw?.trace_id === 'string' ? raw.trace_id : traceId,
  });
}

const STATUS_CODES: Record<number, ApiErrorCode> = {
  400: 'bad_request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  405: 'method_not_allowed',
  409: 'conflict',
  413: 'payload_too_large',
  422: 'validation_error',
  429: 'rate_limited',
  500: 'internal_error',
};

function statusFallbackCode(status: number): ApiErrorCode {
  return STATUS_CODES[status] ?? 'http_error';
}

/** Estrecha un `unknown` de un `catch` o de TanStack Query. */
export function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError({
    status: 0,
    code: 'network_error',
    message: error instanceof Error ? error.message : 'Error inesperado.',
    cause: error,
  });
}
